import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import { useGetParams } from './hooks/useGetParams';
import { useBroadcast } from './hooks/useBroadcast';
import { useNoiseLevel } from './hooks/useNoiseLevel';
import { usePrevRecord } from './hooks/usePrevRecord';

import { NoizeValue } from './components/NoizeValue/NoizeValue';
import NoiseMeter from './components/NoiseMeter/NoiseMeter';
import { NewRecord } from './components/NewRecord/NewRecord';
import { Player } from './components/Player/Player';
import { MatchScore } from './components/MatchScore/MatchScore';

import posterDefault from './assets/images/bgPoster.webp';
import bgDefaultVideo from './assets/video/bgVideo.webm';
import { VideoNewRecord } from './components/VideoNewRecord/VideoNewRecord';

const maxRecordState = 'S12';
const microphonMuterState = 'S13';
const deltaStates = ['S1', 'S2', 'S3', 'S4', 'S5'];
const videoPlayer = ['S7', 'S8', 'S9', 'S10', 'S11'];

function App() {
  const { currentState, allStates, prevRecord, description } = useBroadcast();

  const prevDeltaRef = useRef(0);

  const {
    bgVideo,
    bgPoster,
    calibrationOffset,
    deltaDuration,
    smoothingFactor,
    maxNoiseLevel,
  } = useGetParams();

  const {
    calibratedLevelDbSPL,
    currentMicrophone,
    addSmoothBoost,
    stopMeasurement,
    startMeasurement,
    isActiveMeasurement,
    startFake,
    stopFake,
    isActiveFake,
    maxCalibratedLevelDbSPL,
    setCalibratedLevelDbSPL,
  } = useNoiseLevel({
    calibrationOffset,
    deltaDuration,
    smoothingFactor,
  });

  const { setNoizeLevel } = usePrevRecord({
    calibratedLevelDbSPL,
    setCalibratedLevelDbSPL,
    prevRecord,
    deltaDuration,
  });

  // useEffect(() => {
  //   if (currentState === maxRecordState && isActiveMeasurement) {
  //     stopMeasurement();
  //   } else if (currentState === microphonMuterState && isActiveMeasurement) {
  //     stopMeasurement();
  //   } else if (
  //     currentState !== microphonMuterState &&
  //     currentState !== maxRecordState
  //   ) {
  //     if (!isActiveMeasurement) {
  //       startMeasurement();
  //     }
  //   }
  // }, [currentState, isActiveMeasurement]);

  useEffect(() => {
    if (currentState === microphonMuterState) {
      if (isActiveMeasurement) {
        stopMeasurement();
      }
    }

    if (currentState === microphonMuterState && !isActiveFake) {
      startFake();
      addSmoothBoost(-prevDeltaRef.current);
      prevDeltaRef.current = 0;
    } else if (currentState !== microphonMuterState) {
      if (isActiveFake) {
        stopFake();
      }
    }
  }, [currentState, isActiveFake, isActiveMeasurement]);

  useEffect(() => {
    if (currentState !== maxRecordState) {
      return;
    }

    if (isActiveMeasurement) {
      stopMeasurement();
    }

    setNoizeLevel(Number(maxCalibratedLevelDbSPL));
  }, [
    currentState,
    isActiveMeasurement,
    isActiveFake,
    maxCalibratedLevelDbSPL,
  ]);

  useEffect(() => {
    if (currentState !== 'S6') {
      return;
    }
    if (isActiveMeasurement) {
      stopMeasurement();
    }

    setNoizeLevel(prevRecord);
  }, [currentState, isActiveMeasurement, prevRecord]);

  useEffect(() => {
    if (
      !deltaStates.includes(currentState) &&
      !videoPlayer.includes(currentState)
    ) {
      return;
    }

    if (!isActiveMeasurement) {
      startMeasurement();
    }

    if (deltaStates.includes(currentState) && allStates && currentState) {
      let deltaFromName = 0;

      if (currentState === 'S1') {
        deltaFromName = 0;
      } else {
        deltaFromName = Number(allStates[currentState]?.value || 0) || 0;
      }

      if (deltaFromName !== prevDeltaRef.current) {
        const delta = deltaFromName - prevDeltaRef.current;
        prevDeltaRef.current = deltaFromName;
        addSmoothBoost(delta);
      }
    }
  }, [currentState, allStates, addSmoothBoost, isActiveMeasurement]);

  useEffect(() => {
    if (currentMicrophone?.label) {
      console.log(currentMicrophone?.label);
    }
  }, [currentMicrophone]);

  return (
    <div className="app-wrapper">
      <div className="titre-wrapper">
        <div className="noize-wrapper">
          <div className="noizeValueBlock">
            {/* всегда должен быть, чтобы набирался плавно новый рекорд */}
            <motion.div
              className="noizeValue"
              initial={{ opacity: 0 }}
              animate={{
                opacity:
                  currentState !== maxRecordState
                    ? 1
                    : [1, 0, 1, 0, 1, 0, 1, 0],
              }}
              transition={{
                duration: currentState !== maxRecordState ? 0.5 : 2,
                delay:
                  currentState !== maxRecordState ? 0.5 : deltaDuration / 1000,
              }}
            >
              {calibratedLevelDbSPL > 0 && (
                <NoizeValue noiseLevel={calibratedLevelDbSPL} />
              )}
            </motion.div>

            <AnimatePresence mode="wait">
              {currentState === maxRecordState && calibratedLevelDbSPL && (
                <motion.div
                  key="noizeRecord"
                  exit={{ scale: 0, transition: { duration: 0.5 } }}
                  className="newRecordBlock"
                >
                  <NewRecord
                    noiseLevel={maxCalibratedLevelDbSPL}
                    deltaDuration={deltaDuration}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NoiseMeter
            noiseLevel={calibratedLevelDbSPL}
            prevRecord={prevRecord}
            maxNoiseLevel={maxNoiseLevel}
            visibleRecord={currentState !== maxRecordState}
          />
        </div>
      </div>

      <AnimatePresence>
        {currentState === maxRecordState && (
          <motion.div
            key={'videoNewRecord'}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: 0.5, delay: 2 + deltaDuration / 1000 },
            }}
            exit={{ opacity: 0, transition: { duration: 0.5, delay: 0 } }}
          >
            <VideoNewRecord delay={2000 + deltaDuration} />
          </motion.div>
        )}

        {currentState !== maxRecordState && (
          <motion.div
            key={'player'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.5, delay: 0 } }}
            exit={{
              opacity: 0,
              transition: { duration: 0.5, delay: 2 + deltaDuration / 1000 },
            }}
          >
            <Player currentState={currentState} allStates={allStates} />
          </motion.div>
        )}
      </AnimatePresence>

      {bgVideo && (
        <video
          autoPlay="autoplay"
          muted="muted"
          loop
          id="bgVideo"
          poster={bgVideo === '1' ? posterDefault : bgPoster}
        >
          <source
            src={bgVideo === '1' ? bgDefaultVideo : bgVideo}
            type="video/webm"
          ></source>
        </video>
      )}
    </div>
  );
}

export default App;
